import React from "react";

import { ONGOING_STATES } from "../constants";
import { useEmbedParametersContext } from "../contexts/embedParametersContext";
import { State } from "../enums";
import { localizedText } from "../helpers/previewHelpers";
import i18n from "../service/i18n";
import { usePlayerStore } from "../store/usePlayerStore";
import {
  PlayerEmbedParams,
  PlayerRefreshEvent,
  PlayerUpdateEvent,
  SetPlayerApiFn
} from "../types/common";
import { Media } from "../types/player-api";

import { ErrorOverlay } from "./Error/ErrorOverlay/ErrorOverlay";
import { NonPlayableVideo } from "./NonPlayable/NonPlayableVideo";
import { PreviewAspectRatio } from "./AspectRatio";
import ContentRatingIcons from "./ContentRatingIcons";
import { PreviewImage } from "./PreviewImage";
import { YlePlayer } from "./YlePlayer";

const parseAccessibleDescription = (
  embedParams: PlayerEmbedParams,
  mediaData: Media | undefined,
  isOngoing: boolean
) => {
  const { backgroundVideo, language, title } = embedParams;

  const videoPlayerType = backgroundVideo
    ? i18n("video_player_illustrational", { language })
    : i18n("video_player", { language });

  if (title && isOngoing) {
    return videoPlayerType; // The title will be shown on video
  }

  const accessibleDescription =
    localizedText(mediaData?.title, language) ||
    localizedText(mediaData?.description, language) ||
    "";

  return `${accessibleDescription}${accessibleDescription && ", "}${videoPlayerType}`;
};

// eslint-disable-next-line complexity
export const PreviewVideo = ({
  refreshComponent,
  setPlayerApi,
  setPlayerApiSimple,
  state,
  update
}: {
  refreshComponent: PlayerRefreshEvent;
  setPlayerApi: SetPlayerApiFn;
  setPlayerApiSimple: () => void;
  state: State;
  update: PlayerUpdateEvent;
}) => {
  const { useStoreState } = usePlayerStore();
  const isPlaybackStarted = useStoreState.isPlaybackStarted();
  const mediaData = useStoreState.media();

  const embedParams = useEmbedParametersContext();
  const { autoplay, previewImage } = embedParams;

  const isOngoing = ONGOING_STATES.includes(state) && !!mediaData;
  const isInPreviewState = isOngoing && !autoplay && !isPlaybackStarted;
  const showContentRatingIcons =
    !autoplay && !isPlaybackStarted && state === State.ONGOING_ONDEMAND;

  return (
    <PreviewAspectRatio
      aria-label={parseAccessibleDescription(
        embedParams,
        mediaData,
        isOngoing
      )}>
      {previewImage && (isInPreviewState || !isOngoing) && <PreviewImage />}
      {isOngoing && (
        <>
          <ErrorOverlay />
          {showContentRatingIcons && <ContentRatingIcons />}
          <YlePlayer
            setPlayerApi={setPlayerApi}
            update={update}
            refreshComponent={refreshComponent}
          />
        </>
      )}
      {!isOngoing && (
        <NonPlayableVideo
          refreshComponent={refreshComponent}
          setPlayerApiSimple={setPlayerApiSimple}
          state={state}
        />
      )}
    </PreviewAspectRatio>
  );
};
